'use client'
import {
  createContext,
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useMutation as useApolloMutation } from '@apollo/client'
import { useLazyQuery } from '../../core/hook/useLazyQuery'
import { Cart } from '../../domain/cart'
import { CartDocument } from '../../queries/Cart.gql'
import { CreateEmptyCartDocument } from '../../queries/CreateEmptyCart.gql'
import useCurrentLocale from '../../core/hook/useCurrentLocale'
import { deleteCookie, getCookie, setCookie } from 'cookies-next'
import { handleError } from '../../core/hook/utils'
import useCurrentStore from '../../core/hook/useCurrentStore'
import { useTranslation } from './ConfigContext'

export type ContextResponse =
  | {
    success: true
    cart?: Cart
  }
  | {
    success: false
    error: string
    cart?: Cart
  }

const modalUuid = '4a255c38-778a-4299-af8e-c02f6b135492';

export type CartContextType = {
  cart: Cart
  fixItemsTitle(cart: Cart): Cart
  modalUuid: string,
  hasDifferentAddresses: boolean
  setHasDifferentAddresses(v: boolean): void
  billingAddressSaved: boolean
  setBillingAddressSaved(v: boolean): void
  /* eslint-disable no-unused-vars */
  setCart: (cart: Cart) => void
  updateCart: (cart) => void
  resetCart: () => void
  createCart: () => Promise<string | null>
  /* eslint-disable no-unused-vars */
  hydrateCart: (cartId: string | null) => void
  removeGiftMessage: () => void
}

export const defaultCart = {
  id: null,
  email: null,
  items: [],
  applied_coupons: [],
  with_card: false,
  gift_message: {
    message: null,
  },
  prices: {
    discounts: [],
    applied_taxes: [],
    grand_total: {
      value: 0,
      currency: 'EUR',
    },
  },
  shipping_addresses: [],
  billing_address: null,
  available_payment_methods: [],
  delivery_date: 0,
  delivery_time: '',
  gtm_categories: '',
}

export const CartContext = createContext<CartContextType>({
  cart: defaultCart,
  fixItemsTitle: (cart) => cart,
  modalUuid,
  billingAddressSaved: false,
  setBillingAddressSaved: () => { },
  hasDifferentAddresses: false,
  setHasDifferentAddresses: () => { },
  setCart: () => { },
  updateCart: () => { },
  resetCart: () => { },
  createCart: () =>
    new Promise((resolve) => {
      resolve(null)
    }),
  /* eslint-disable no-unused-vars */
  hydrateCart: () => { },
  removeGiftMessage: () => { },
})

const CartProvider: FunctionComponent<{ children: ReactNode }> = ({ children }) => {
  const [createEmptyCart] = useApolloMutation(CreateEmptyCartDocument)
  const locale = useCurrentLocale()
  const store = useCurrentStore()
  const [hasDifferentAddresses, setHasDifferentAddresses] = useState(false)
  const [billingAddressSaved, setBillingAddressSaved] = useState(false)
  const t = useTranslation()

  const cartKey = `cart-${store.region_code}`
  // Migrate old cart keys
  if (typeof window !== 'undefined') {
    const prevCartId = getCookie('cart')
    if (prevCartId) {
      deleteCookie('cart')
      setCookie(cartKey, prevCartId)
    }
    const prevCart = localStorage.getItem('cart')
    if (prevCart) {
      localStorage.removeItem('cart')
      localStorage.setItem(cartKey, prevCart)
    }
  }

  const fixItemsTitle = useCallback((cart: Cart) => {
    return {
      ...cart,
      items:
        cart.items.map((item) => {
          const title = item.product?.title?.replace('{{ product-subtitle-category }}', t('product-subtitle-category'));
          return {
            ...item,
            product: {
              ...item.product,
              title,
            },
          };
        })
    };
  }, [t]);

  const [cart, doSetCart] = useState<Cart>(() => {
    if (typeof localStorage !== 'undefined') {
      const localCart =
        getCookie(cartKey) && (JSON.parse(localStorage.getItem(cartKey) ?? '{}') as Cart)

      if (localCart && localCart.id) {
        return localCart
      }
    }

    return defaultCart
  })

  function removeGiftMessage() {
    doSetCart((prev) => ({
      ...prev,
      gift_message: null,
      with_card: false,
    }))
  }

  function updateCart(updateCart) {
    const newCart: Cart = {
      ...cart,
      ...updateCart,
    }

    return setCart(newCart)
  }

  function setCart(newCart: Cart) {
    if (
      !newCart.shipping_addresses ||
      newCart.shipping_addresses.length === 0 ||
      !newCart.billing_address
    ) {
      // This was commented because it was removing billing address when validating the step
      // setHasDifferentAddresses(false)
      // setBillingAddressSaved(false)
    }
    doSetCart(newCart)
    if (newCart.id) {
      localStorage.setItem(cartKey, JSON.stringify(newCart))
      setCookie(cartKey, newCart.id)
    }
  }

  const [getCart] = useLazyQuery(CartDocument, {
    fetchPolicy: 'no-cache',
  })

  const createCart = useCallback(
    async (): Promise<string | null> =>
      //use Apollo Mutation because useMutation have bug a this moment and userToken is undefined
      createEmptyCart({
        context: {
          headers: {
            Authorization: getCookie('userToken') ? `Bearer ${getCookie('userToken')}` : '',
            Store: locale,
          },
        },
      })
        .then((data) => {
          if (data.data?.createEmptyCart) {
            return data.data?.createEmptyCart
          }

          return null
        })
        .catch((error) => {
          handleError(error, false)

          return null
        }),
    [createEmptyCart],
  )

  const resetCart = useCallback(() => {
    setCart(defaultCart)
    deleteCookie(cartKey)
    localStorage.removeItem('cart')
    localStorage.removeItem(cartKey)
  }, [])

  useEffect(() => {
    const cartId = getCookie(cartKey) as string

    if (cartId) {
      hydrateCart(cartId)
    }
  }, [])

  const hydrateCart = useCallback(
    async (cartId: string | null) => {
      getCart({
        variables: {
          cartId: String(cartId) || cart.id || '',
        },
      })
        .then((response) => {
          if (response.data?.cart) {
            //@ts-ignore
            setCart(response.data.cart)
          } else {
            throw 'cart not found'
          }
        })
        .catch((error) => {
          setCart(defaultCart)
          handleError(error, false)
        })
    },
    [getCart],
  )

  const context = useMemo(
    () => ({
      cart,
      modalUuid,
      hasDifferentAddresses,
      setHasDifferentAddresses,
      billingAddressSaved,
      setBillingAddressSaved,
      resetCart,
      createCart,
      setCart,
      updateCart,
      hydrateCart,
      removeGiftMessage,
      fixItemsTitle,
    }),
    [cart, resetCart, createCart, setCart],
  )

  return <CartContext.Provider value={context}>{children}</CartContext.Provider>
}

export default CartProvider
